import { useState, useCallback } from 'react';
import { SignUp } from './SignUp';
import WizardContainer from './wizard/WizardContainer';
import { CharacterEditorInline } from './CharacterEditor/CharacterEditorInline';
import { useUser } from '../context/UserContext';
import { profileService } from '../services/api';
import { normalizeGender } from '../types/user';
import { DEFAULT_GUIDE_SLUG } from '../data/guidedNpcs';
import './OnboardingFlow.css';

type OnboardingPhase = 'signup' | 'wizard' | 'character';

interface OnboardingFlowProps {
  /** Called when the whole flow finishes (wizard + character, or skipped). */
  onComplete: () => void;
  onBackToLogin: () => void;
  /**
   * Start directly at the wizard — used for logged-in users resuming an
   * incomplete profile from the "finish your profile" banner.
   */
  startAtWizard?: boolean;
  /**
   * Second door (WU-W5): launch the "go ask Mara about you" guided-NPC flow
   * instead of filling the forms. When provided, a scoped CTA renders under
   * the wizard. The host (App.tsx) unmounts this flow and navigates into the
   * world. Optional so the register flow can omit it if desired.
   */
  onAskNpc?: (slug: string) => void;
}

/**
 * The full onboarding funnel:
 *   1. signup    — account only (SignUp)
 *   2. wizard    — the 7-stage profile wizard; stage 1 is required, the rest
 *                  are skippable so people reveal as much as they want, when
 *                  they want (per-field visibility comes later in the editor)
 *   3. character — build the 3D look used in rooms (skippable)
 */
export function OnboardingFlow({ onComplete, onBackToLogin, startAtWizard = false, onAskNpc }: OnboardingFlowProps) {
  const { user, updateProfile } = useUser();
  // If an authenticated user already exists, the account step is done — start
  // at the wizard. This also heals the register-flow remount: UserContext
  // toggles the global isLoading during register, which swaps AppContent to
  // the spinner and unmounts this component mid-flow; on remount `user` is
  // set, so we resume at the wizard instead of showing the signup form again.
  const [phase, setPhase] = useState<OnboardingPhase>(startAtWizard || user ? 'wizard' : 'signup');

  /**
   * Mirror the wizard's dating-profile essentials onto the legacy SignalR
   * profile so room join fallbacks (gender-based character) and discovery
   * keep working. The dating profile (REST) is the source of truth; the
   * legacy profile just follows it.
   */
  const syncLegacyProfile = useCallback(async () => {
    try {
      const datingProfile = await profileService.getProfile();
      if (!datingProfile || !user) return;
      await updateProfile({
        age: datingProfile.age || user.age,
        gender: datingProfile.gender ? normalizeGender(datingProfile.gender) : user.gender,
        bio: datingProfile.bio ?? user.bio,
        location: [datingProfile.locationCity, datingProfile.locationCountry]
          .filter(Boolean)
          .join(', ') || user.location,
      });
    } catch (e) {
      // Non-fatal: the dating profile still holds the data.
      console.warn('[Onboarding] Legacy profile sync failed:', e);
    }
  }, [user, updateProfile]);

  const handleWizardDone = useCallback(async () => {
    await syncLegacyProfile();
    setPhase('character');
  }, [syncLegacyProfile]);

  if (phase === 'signup') {
    return <SignUp onComplete={() => setPhase('wizard')} onBackToLogin={onBackToLogin} />;
  }

  if (phase === 'wizard') {
    return (
      <div className="onboarding-shell">
        {/* V2 Amethyst WU-02 (R7): the wizard is "who you are" — the product
            itself, not a form to get through before the "real" part. */}
        <div className="onboarding-wizard-intro">
          <h2>Who are you?</h2>
          <p>The person is the point here — the game room is just where you'll meet people.</p>
        </div>
        <WizardContainer onComplete={handleWizardDone} onSkip={handleWizardDone} />
        {onAskNpc && (
          <button
            type="button"
            className="onboarding-second-door"
            onClick={() => onAskNpc(DEFAULT_GUIDE_SLUG)}
          >
            Prefer to just talk? Go ask Mara about you →
          </button>
        )}
      </div>
    );
  }

  // phase === 'character' — V2 Amethyst WU-02 (R7): framed explicitly as the
  // "addition," not the destination — the wizard just finished being who you are.
  return (
    <div className="onboarding-shell onboarding-character">
      <div className="onboarding-character-header">
        <h2>Dress up for the venue</h2>
        <p>
          You've told us who you are — this is just the costume you'll wear when you show up.
          Shape the body, face, hair and outfit, or skip and use the default; you can always
          come back via Profile → Game.
        </p>
        <button className="onboarding-continue-btn" onClick={onComplete}>
          Continue to GameDate →
        </button>
      </div>
      <div className="onboarding-character-editor">
        <CharacterEditorInline />
      </div>
    </div>
  );
}
